import { useState, useMemo, useEffect } from 'react';

const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (str) => {
  if (!str || str === '0000-00-00 00:00:00') return '';
  const d = new Date(str);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Modal for selecting invoice(s) to pay via Zoho hosted payment page.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {Array}   props.invoices       — Unpaid + partially paid invoices
 * @param {boolean} props.submitting     — Pay button disabled + spinner state
 * @param {function} props.onClose
 * @param {function} props.onConfirm     — receives { invoices: [{invoice_id, amount}] }
 */
const PayNowModal = ({ open, invoices, submitting, onClose, onConfirm }) => {
  const [selected, setSelected] = useState({});

  // Default to all invoices selected when the modal opens
  useEffect(() => {
    if (open) {
      const next = {};
      invoices.forEach((inv) => { next[inv.invoice_id] = true; });
      setSelected(next);
    }
  }, [open, invoices]);

  const allSelected = useMemo(
    () => invoices.length > 0 && invoices.every((inv) => selected[inv.invoice_id]),
    [invoices, selected],
  );

  const totalSelected = useMemo(() => {
    return invoices.reduce(
      (sum, inv) => sum + (selected[inv.invoice_id] ? (parseFloat(inv.balance) || 0) : 0),
      0,
    );
  }, [invoices, selected]);

  const selectedCount = useMemo(
    () => invoices.filter((inv) => selected[inv.invoice_id]).length,
    [invoices, selected],
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      invoices.forEach((inv) => { next[inv.invoice_id] = true; });
      setSelected(next);
    }
  };

  const toggleOne = (invoiceId) => {
    setSelected((prev) => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
  };

  const handleConfirm = () => {
    const chosen = invoices
      .filter((inv) => selected[inv.invoice_id])
      .map((inv) => ({
        invoice_id: inv.invoice_id,
        invoice_number: inv['invoice#'] || `INV-${inv.invoice_id}`,
        amount: parseFloat(inv.balance) || 0,
      }));
    if (chosen.length === 0) return;
    onConfirm({ invoices: chosen });
  };

  if (!open) return null;

  return (
    <div className="bp-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="bp-pay-modal-title">
      <div className="bp-modal">
        <div className="bp-modal__header">
          <h2 id="bp-pay-modal-title" className="bp-modal__title">Pay Invoices</h2>
          <button
            type="button"
            className="bp-modal__close"
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bp-modal__body">
          {invoices.length === 0 ? (
            <p className="bp-modal__empty">No unpaid invoices.</p>
          ) : (
            <>
              <p className="bp-modal__instructions">
                Select the invoices you'd like to pay. Selecting all is equivalent to paying your total balance.
              </p>

              <div className="bp-pay-list">
                <label className="bp-pay-row bp-pay-row--header">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={submitting}
                    aria-label="Select all invoices"
                  />
                  <span className="bp-pay-row__invoice">Invoice</span>
                  <span className="bp-pay-row__date">Due Date</span>
                  <span className="bp-pay-row__status">Status</span>
                  <span className="bp-pay-row__amount">Balance</span>
                </label>

                {invoices.map((inv) => {
                  const checked = !!selected[inv.invoice_id];
                  return (
                    <label
                      key={inv.invoice_id}
                      className={`bp-pay-row${checked ? ' bp-pay-row--selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(inv.invoice_id)}
                        disabled={submitting}
                      />
                      <span className="bp-pay-row__invoice">{inv['invoice#'] || `INV-${inv.invoice_id}`}</span>
                      <span className="bp-pay-row__date">{formatDate(inv.due_date)}</span>
                      <span className="bp-pay-row__status">{inv.status}</span>
                      <span className="bp-pay-row__amount">{currencyFmt.format(parseFloat(inv.balance) || 0)}</span>
                    </label>
                  );
                })}
              </div>

              <div className="bp-pay-total">
                <span className="bp-pay-total__label">
                  {selectedCount} {selectedCount === 1 ? 'invoice' : 'invoices'} selected
                </span>
                <span className="bp-pay-total__amount">{currencyFmt.format(totalSelected)}</span>
              </div>
            </>
          )}
        </div>

        <div className="bp-modal__footer">
          <button
            type="button"
            className="bp-modal__btn bp-modal__btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bp-modal__btn bp-modal__btn--primary"
            onClick={handleConfirm}
            disabled={submitting || selectedCount === 0 || totalSelected <= 0}
          >
            {submitting ? (
              <>
                <span className="bp-spinner" aria-hidden="true" />
                Opening payment page…
              </>
            ) : (
              `Pay ${currencyFmt.format(totalSelected)}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayNowModal;
